import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api';
import Swal from 'sweetalert2';
import { useOutletContext } from 'react-router-dom';
import SlidingDrawer from '../../components/SlidingDrawer';

const MinisterAgendas = () => {
    const { currentDept } = useOutletContext();
    const [agendas, setAgendas] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [bills, setBills] = useState([]); // Latest bills only
    const [billVersions, setBillVersions] = useState([]); // Versions related to selected bill

    // Selection State
    const [selectedBillId, setSelectedBillId] = useState(null); // The "Family" ID (latest bill id)
    const [selectedVersionId, setSelectedVersionId] = useState(null); // The actual specific bill ID to link

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [loading, setLoading] = useState(true);

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        sessionId: '',
        documents: []
    });
    const [docInput, setDocInput] = useState({ title: '', url: '' });

    const [editingId, setEditingId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [filter, setFilter] = useState('');
    const [isViewOnly, setIsViewOnly] = useState(false);

    useEffect(() => {
        if (currentDept) {
            fetchAgendas();
            fetchBills();
        }
        fetchSessions();
    }, [currentDept]);

    // When selected bill changes, fetch its versions
    useEffect(() => {
        if (selectedBillId) {
            fetchVersions(selectedBillId);
        } else {
            setBillVersions([]);
            setSelectedVersionId(null);
        }
    }, [selectedBillId]);

    const fetchAgendas = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/minister/agendas`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAgendas(res.data);
        } catch (err) {
            console.error('Failed to fetch agendas', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSessions = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/public/sessions`);
            setSessions(res.data);
        } catch (err) { console.error('Failed to fetch sessions'); }
    };

    const fetchBills = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/minister/bills`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBills(res.data); // These are now only isLatest=true bills
        } catch (err) { console.error('Failed to fetch bills'); }
    };

    const fetchVersions = async (billId) => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/minister/bills/${billId}/versions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBillVersions(res.data);
            // Default to the selected bill as it is the latest, unless editing
            // Only set default if we don't have one selected (e.g. from edit mode)
            if (!selectedVersionId) setSelectedVersionId(billId);
        } catch (err) {
            console.error('Failed to fetch versions');
            setBillVersions([]);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formDataVideo = new FormData();
        formDataVideo.append('file', file);

        setUploading(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/api/upload`, formDataVideo, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setDocInput({ ...docInput, url: res.data.url, title: file.name });
            setUploading(false);
        } catch (err) {
            Swal.fire('Error', 'File upload failed', 'error');
            setUploading(false);
        }
    };

    const handleCreateOrUpdate = async (e) => {
        e.preventDefault();

        // Constraint: Single Bill (Version) per Agenda is now MANDATORY
        if (!selectedVersionId) {
            return Swal.fire({
                title: 'Bill Required',
                text: 'Every legislative agenda must be linked to a specific bill version.',
                icon: 'warning',
                confirmButtonColor: '#C0722C'
            });
        }

        const linkedBills = [selectedVersionId];

        const payload = { ...formData, bills: linkedBills };
        try {
            const token = localStorage.getItem('token');
            if (editingId) {
                await axios.put(`${API_BASE_URL}/api/minister/agendas/${editingId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Updated', 'Agenda Draft Updated', 'success');
            } else {
                await axios.post(`${API_BASE_URL}/api/minister/agendas`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Success', 'Agenda Draft Created', 'success');
            }

            setIsDrawerOpen(false);
            setEditingId(null);
            setFormData({ title: '', description: '', sessionId: '', documents: [] });
            setSelectedBillId(null);
            setSelectedVersionId(null);
            fetchAgendas();
        } catch (err) {
            Swal.fire('Error', 'Operation failed', 'error');
        }
    };

    const openEdit = (agenda) => {
        setFormData({
            title: agenda.title,
            description: agenda.description,
            sessionId: agenda.sessionId?._id || agenda.sessionId,
            documents: agenda.documents || []
        });

        // Handle Bill Pre-selection
        if (agenda.bills && agenda.bills.length > 0) {
            // agenda.bills contains IDs (from initial fetch which is not populated)
            // or Objects if populated. Let's assume IDs based on logs.
            const linkedId = typeof agenda.bills[0] === 'object' ? agenda.bills[0]._id : agenda.bills[0];

            // We need to find the bill family.
            // Since we don't know the family ID easily without fetching bill details,
            // we will fetch versions of this linked bill, which returns the whole family.
            axios.get(`${API_BASE_URL}/api/minister/bills/${linkedId}/versions`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            }).then(res => {
                setBillVersions(res.data);
                // Find the latest one to set as "Family Selected" (Radio)
                const latest = res.data.find(b => b.isLatest);
                if (latest) setSelectedBillId(latest._id);
                // Set the specific version selected
                setSelectedVersionId(linkedId);
            }).catch(e => console.error(e));
        } else {
            setSelectedBillId(null);
            setSelectedVersionId(null);
        }

        // View Mode Logic
        if (agenda.status === 'DRAFT' || agenda.status === 'RETURNED') {
            setIsViewOnly(false);
        } else {
            setIsViewOnly(true);
        }

        setEditingId(agenda._id);
        setIsDrawerOpen(true);
    };

    const handleSubmit = async (id) => {
        const result = await Swal.fire({
            title: 'Confirm Submission',
            text: 'This will lock the agenda and send it to the Secretariat.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#C0722C',
            confirmButtonText: 'Yes, Submit'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.put(`${API_BASE_URL}/api/minister/agendas/${id}`, { status: 'SUBMITTED' }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Submitted', 'Agenda sent successfully', 'success');
                fetchAgendas();
            } catch (err) {
                Swal.fire('Error', 'Submission failed', 'error');
            }
        }
    };

    const addDoc = () => {
        if (docInput.title && docInput.url) {
            setFormData({ ...formData, documents: [...formData.documents, docInput] });
            setDocInput({ title: '', url: '' });
        }
    };

    const removeDoc = (index) => {
        const newDocs = [...formData.documents];
        newDocs.splice(index, 1);
        setFormData({ ...formData, documents: newDocs });
    };

    // UI Helpers
    const getStatusStyle = (status) => {
        switch (status) {
            case 'SUBMITTED': return { bg: '#E3F2FD', text: '#1565C0', border: 'rgba(21, 101, 192, 0.2)' };
            case 'FORWARDED_TO_SPEAKER': return { bg: '#E8F5E9', text: '#2E7D32', border: 'rgba(46, 125, 50, 0.2)' };
            case 'RETURNED': return { bg: '#FFEBEE', text: '#C62828', border: 'rgba(198, 40, 40, 0.2)' };
            default: return { bg: '#FDF8F3', text: '#E65100', border: 'rgba(230, 81, 0, 0.2)' };
        }
    };

    const filteredAgendas = agendas.filter(a =>
        a.title.toLowerCase().includes(filter.toLowerCase()) ||
        (a.description && a.description.toLowerCase().includes(filter.toLowerCase()))
    );

    return (
        <div style={{ fontFamily: '"Outfit", sans-serif', maxWidth: '1400px', margin: '0 auto', overflowX: 'hidden' }}>
            <style>
                {`
                .custom-input {
                    width: 100%;
                    padding: 16px 20px;
                    border-radius: 12px;
                    border: 1.5px solid #E5E5E5;
                    font-size: 16px;
                    box-sizing: border-box;
                    outline: none;
                    transition: all 0.3s ease;
                    background-color: #FAFAFA;
                    font-family: inherit;
                    color: #333;
                }
                .custom-input:focus {
                    border-color: #C0722C;
                    box-shadow: 0 0 0 4px rgba(192, 114, 44, 0.1);
                    background-color: #fff;
                    background-color: #fff;
                }
                .shimmer-loader {
                    border: 4px solid #F3F3F3;
                    border-top: 4px solid #C0722C;
                    border-radius: 50%;
                    width: 40px;
                    height: 40px;
                    animation: spin-loader 1s linear infinite;
                }
                @keyframes spin-loader {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                `}
            </style>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h1 style={{ margin: '0 0 8px 0', fontSize: '32px', fontWeight: '800', color: '#2C1A10' }}>Agenda Management</h1>
                    <p style={{ margin: 0, color: '#999', fontSize: '16px' }}>
                        Executive Department: <span style={{ color: '#2C1A10', fontWeight: '700' }}>{currentDept?.name}</span>
                    </p>
                </div>
                <button
                    onClick={() => {
                        setEditingId(null);
                        setFormData({ title: '', description: '', sessionId: '', documents: [] });
                        setSelectedBillId(null);
                        setSelectedVersionId(null);
                        setIsDrawerOpen(true);
                        setIsViewOnly(false);
                    }}
                    style={{
                        padding: '14px 28px',
                        backgroundColor: '#2C1A10',
                        color: 'white',
                        border: 'none',
                        borderRadius: '16px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                    }}
                >
                    + New Agenda
                </button>
            </div>

            <div style={{ position: 'relative', marginBottom: '45px' }}>
                <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#C0722C', display: 'flex', zIndex: 2 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                </div>
                <input
                    type="text"
                    placeholder="Search Agendas by Title, Content, or Reference..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="custom-input"
                    style={{ paddingLeft: '48px' }}
                />
            </div>

            <div style={{
                backgroundColor: 'white',
                borderRadius: '28px',
                boxShadow: '0 15px 45px rgba(0,0,0,0.03)',
                overflow: 'hidden',
                border: '1px solid #F0F0F0',
                minHeight: '400px'
            }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', gap: '20px' }}>
                        <div className="shimmer-loader"></div>
                        <div style={{ fontSize: '11px', color: '#888', fontWeight: '800', letterSpacing: '2px', textTransform: 'uppercase' }}>Synchronizing Repository...</div>
                    </div>
                ) : filteredAgendas.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '450px', textAlign: 'center' }}>
                        <h3 style={{ color: '#2C1A10', margin: '0 0 12px 0', fontSize: '24px', fontWeight: '800' }}>Registry is Clear</h3>
                        <p style={{ color: '#888', fontSize: '16px', margin: 0 }}>No agenda entries found for "<strong>{filter}</strong>".</p>
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', textAlign: 'left' }}>
                        <thead style={{ backgroundColor: '#FDF8F3' }}>
                            <tr style={{ color: '#888', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                                <th style={{ padding: '25px 30px' }}>Agenda Identity</th>
                                <th style={{ padding: '25px 30px' }}>Session / Context</th>
                                <th style={{ padding: '25px 30px' }}>Status Matrix</th>
                                <th style={{ padding: '25px 30px', textAlign: 'right' }}>Directives</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAgendas.map(item => {
                                const styles = getStatusStyle(item.status);
                                return (
                                    <tr key={item._id} style={{ borderBottom: '1px solid #F5F5F5' }}>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ fontWeight: '800', color: '#2C1A10', fontSize: '17px' }}>{item.title}</div>
                                            <div style={{ fontSize: '14px', color: '#777' }}>{item.description}</div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ fontSize: '15px', color: '#2C1A10', fontWeight: '700' }}>{item.sessionId?.name || 'General Session'}</div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <span style={{ padding: '6px 14px', borderRadius: '10px', backgroundColor: styles.bg, color: styles.text, fontSize: '11px', fontWeight: '800', textTransform: 'uppercase' }}>
                                                {item.status.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td style={{ padding: '25px 30px', textAlign: 'right' }}>
                                            {(item.status === 'DRAFT' || item.status === 'RETURNED') ? (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                                    <button onClick={() => openEdit(item)} style={{ padding: '10px 20px', backgroundColor: '#fff', border: '1.5px solid #E5E5E5', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}>Edit Plan</button>
                                                    <button onClick={() => handleSubmit(item._id)} style={{ padding: '10px 20px', backgroundColor: '#2C1A10', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}>Submit</button>
                                                </div>
                                            ) : (
                                                <button onClick={() => openEdit(item)} style={{ padding: '10px 20px', backgroundColor: '#FDF8F3', color: '#C0722C', border: '1px solid #F0E8DB', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' }}>View Record</button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <SlidingDrawer
                isOpen={isDrawerOpen}
                onClose={() => { setIsDrawerOpen(false); setEditingId(null); }}
                title={isViewOnly ? "Agenda Review Dossier" : (editingId ? "Edit Agenda Draft" : "New Agenda Draft")}
            >
                {isViewOnly ? (
                    <div style={{ animation: 'fadeIn 0.4s ease' }}>
                        {/* Header Section */}
                        <div style={{ padding: '25px', backgroundColor: '#FDF8F3', borderRadius: '20px', border: '1px solid #F0E8DB', marginBottom: '30px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                                <div>
                                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Agenda Subject Matter</div>
                                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#2C1A10', lineHeight: '1.4' }}>{formData.title}</div>
                                </div>
                                <div style={{
                                    padding: '6px 12px',
                                    backgroundColor: getStatusStyle(agendas.find(a => a._id === editingId)?.status).bg,
                                    color: getStatusStyle(agendas.find(a => a._id === editingId)?.status).text,
                                    borderRadius: '10px',
                                    fontSize: '10px',
                                    fontWeight: '800',
                                    textTransform: 'uppercase'
                                }}>
                                    {agendas.find(a => a._id === editingId)?.status.replace(/_/g, ' ')}
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Agenda Narrative</div>
                                <div style={{ fontSize: '15px', color: '#444', lineHeight: '1.7', whiteSpace: 'pre-line' }}>{formData.description}</div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div>
                                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '5px' }}>Department Context</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{currentDept?.name}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '5px' }}>Legislative Session</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{sessions.find(s => s._id === formData.sessionId)?.name || 'N/A'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Linked Bill Section */}
                        {selectedVersionId && (
                            <div style={{ marginBottom: '30px' }}>
                                <div style={{ fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '15px' }}>Linked Legislative Bill</div>
                                {(() => {
                                    const bill = billVersions.find(v => v._id === selectedVersionId);
                                    if (!bill) return null;
                                    return (
                                        <div style={{
                                            padding: '25px',
                                            backgroundColor: '#fff',
                                            border: '1.5px solid #F0F0F0',
                                            borderRadius: '24px',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', backgroundColor: '#C0722C' }}></div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                                                <div>
                                                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '5px' }}>
                                                        {bill.type || 'Legislative'} Bill
                                                    </div>
                                                    <div style={{ fontSize: '19px', fontWeight: '800', color: '#2C1A10', lineHeight: '1.3' }}>{bill.title}</div>
                                                </div>
                                                <div style={{
                                                    padding: '5px 12px',
                                                    backgroundColor: '#E3F2FD',
                                                    color: '#1565C0',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    fontWeight: '800',
                                                    border: '1px solid rgba(21, 101, 192, 0.1)'
                                                }}>
                                                    Version {bill.version?.toFixed(1) || '1.0'}
                                                </div>
                                            </div>

                                            <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '22px', whiteSpace: 'pre-line' }}>
                                                {bill.description || 'No additional description provided.'}
                                            </div>

                                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                <a href={bill.documentUrl} target="_blank" rel="noreferrer" style={{
                                                    flex: 1,
                                                    textDecoration: 'none',
                                                    padding: '14px 20px',
                                                    backgroundColor: '#2C1A10',
                                                    borderRadius: '14px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '10px',
                                                    color: 'white',
                                                    fontWeight: '700',
                                                    fontSize: '13px',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 5px 15px rgba(44, 26, 16, 0.15)'
                                                }}
                                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#C0722C'}
                                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2C1A10'}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                                    View Bill Certificate
                                                </a>
                                                <div style={{
                                                    padding: '12px 16px',
                                                    backgroundColor: '#FDF8F3',
                                                    borderRadius: '12px',
                                                    color: '#C0722C',
                                                    fontSize: '12px',
                                                    fontWeight: '700',
                                                    border: '1px solid rgba(192, 114, 44, 0.1)'
                                                }}>
                                                    {bill.language || 'English'}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* Supporting Documents Section */}
                        <div style={{ marginBottom: '30px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '15px' }}>Supporting Documents</div>
                            {formData.documents && formData.documents.length > 0 ? (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                                    {formData.documents.map((doc, idx) => (
                                        <a key={idx} href={doc.url} target="_blank" rel="noreferrer" style={{
                                            textDecoration: 'none',
                                            padding: '16px 20px',
                                            backgroundColor: '#fff',
                                            border: '1.5px solid #F0F0F0',
                                            borderRadius: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            transition: 'all 0.2s'
                                        }} onMouseOver={(e) => e.currentTarget.style.borderColor = '#C0722C'}>
                                            <div style={{ background: '#FDF8F3', padding: '10px', borderRadius: '12px', color: '#C0722C' }}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{doc.title}</div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>Certified PDF Attachment • Click to View</div>
                                            </div>
                                            <div style={{ marginLeft: 'auto', color: '#CCC' }}>↗</div>
                                        </a>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#F9F9F9', borderRadius: '16px', border: '1px dashed #E0E0E0', color: '#999', fontSize: '14px' }}>
                                    No documents attached to this dossier.
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleCreateOrUpdate}>
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase' }}>Legislative Session</label>
                            <select
                                value={formData.sessionId}
                                onChange={e => setFormData({ ...formData, sessionId: e.target.value })}
                                required
                                style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #eee', fontSize: '14px', backgroundColor: '#FAFAFA' }}
                            >
                                <option value="">Select Session</option>
                                {Array.isArray(sessions) && sessions.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                            </select>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase' }}>Agenda Title</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={e => setFormData({ ...formData, title: e.target.value })}
                                required
                                style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #eee', fontSize: '14px' }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase' }}>Description</label>
                            <textarea
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                style={{ width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #eee', minHeight: '120px', fontSize: '14px' }}
                            />
                        </div>

                        {/* Proposal Bill Attachment - Single Select + Version */}
                        <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#F9F9F9', borderRadius: '16px', border: '1px solid #EEE' }}>
                            <label style={{ display: 'block', marginBottom: '15px', fontSize: '12px', fontWeight: '800', color: '#555', textTransform: 'uppercase' }}>Legislative Bill Proposal (Mandatory)</label>
                            {bills.length === 0 ? (
                                <div style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>No active bills found for this session.</div>
                            ) : (
                                <div style={{ display: 'grid', gap: '10px' }}>
                                    {bills.map(bill => (
                                        <div key={bill._id}>
                                            <div
                                                onClick={() => setSelectedBillId(selectedBillId === bill._id ? null : bill._id)}
                                                style={{
                                                    padding: '12px 14px',
                                                    backgroundColor: selectedBillId === bill._id ? '#FFF3E0' : '#fff',
                                                    border: selectedBillId === bill._id ? '1.5px solid #E65100' : '1px solid #ddd',
                                                    borderRadius: '10px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px'
                                                }}
                                            >
                                                <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: `1.5px solid ${selectedBillId === bill._id ? '#E65100' : '#ccc'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {selectedBillId === bill._id && <div style={{ width: '10px', height: '10px', backgroundColor: '#E65100', borderRadius: '50%' }}></div>}
                                                </div>
                                                <div>
                                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#333' }}>{bill.title}</div>
                                                </div>
                                            </div>

                                            {/* Version Selector Dropdown if Selected */}
                                            {selectedBillId === bill._id && (
                                                <div style={{ marginTop: '8px', marginLeft: '30px' }}>
                                                    <select
                                                        value={selectedVersionId || ''}
                                                        onChange={(e) => setSelectedVersionId(e.target.value)}
                                                        style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px' }}
                                                    >
                                                        {billVersions.map(v => (
                                                            <option key={v._id} value={v._id}>
                                                                Version {v.version.toFixed(1)} {v.isLatest ? '(Latest)' : ''} - {v.status}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Document Upload section remains same */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '12px', fontWeight: '800', color: '#888', textTransform: 'uppercase' }}>Documents</label>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                                <input type="file" onChange={handleFileUpload} style={{ display: 'none' }} id="doc-upload" />
                                <label htmlFor="doc-upload" style={{ padding: '10px 15px', backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#444' }}>
                                    {uploading ? 'Uploading...' : 'Upload File'}
                                </label>
                                <input
                                    placeholder="Document Title"
                                    value={docInput.title}
                                    onChange={e => setDocInput({ ...docInput, title: e.target.value })}
                                    style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '13px' }}
                                />
                                <button type="button" onClick={addDoc} disabled={!docInput.url} style={{ padding: '10px 15px', backgroundColor: docInput.url ? '#2C1A10' : '#eee', color: 'white', border: 'none', borderRadius: '10px', cursor: docInput.url ? 'pointer' : 'not-allowed' }}>Add</button>
                            </div>
                            {formData.documents.map((d, i) => (
                                <div key={i} style={{ fontSize: '13px', padding: '10px', background: '#fff', marginBottom: '8px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #eee' }}>
                                    <span>📎 {d.title}</span>
                                    <span onClick={() => removeDoc(i)} style={{ cursor: 'pointer', color: '#ccc' }}>×</span>
                                </div>
                            ))}
                        </div>

                        <button type="submit" style={{ width: '100%', padding: '16px', backgroundColor: '#C0722C', color: 'white', border: 'none', borderRadius: '14px', fontWeight: '800', fontSize: '15px', cursor: 'pointer' }}>
                            {editingId ? 'Update Draft' : 'Update Agenda'}
                        </button>
                    </form>
                )}
            </SlidingDrawer>
        </div>
    );
};

export default MinisterAgendas;
